import { takeEvery, put, fork, all, call } from "redux-saga/effects";
import { v4 as uuidv4 } from "uuid";
import {
  ProcessActionTypes,
  SubmitStepConfigRequestAction,
  SubmitFeedbackRequestAction,
  DeleteStepRequestAction,
} from "./types";
import * as actions from "./actions";
import * as api from "../../services/process/api";
import { WorkflowCategoryData, FeedbackEntry } from "../../types/process";
import { getLoggedinUser } from "../../api/apiCore";

// Fetch Workflows Saga
function* fetchWorkflowsSaga() {
  try {
    const response: WorkflowCategoryData[] = yield call(api.fetchWorkflowData);
    yield put(actions.fetchWorkflowsSuccess(response));
  } catch (error: any) {
    yield put(
      actions.fetchWorkflowsFailure(
        error.message || "Failed to fetch workflows",
      ),
    );
  }
}

// Submit Step Configuration Saga
function* submitStepConfigSaga(action: SubmitStepConfigRequestAction) {
  try {
    // Dispatch optimistic update immediately
    yield put(actions.optimisticUpdateStepConfig(action.payload));

    // Make API call
    const response: WorkflowCategoryData[] = yield call(
      api.submitStepConfiguration,
      action.payload,
    );

    // Success - just mark loading as false, keep optimistic update
    yield put(actions.submitStepConfigSuccess(response));
  } catch (error: any) {
    // Even on failure, we keep the optimistic update as requested
    yield put(
      actions.submitStepConfigFailure(
        error.message || "Failed to submit step configuration",
      ),
    );
  }
}

// Submit Feedback Saga
function* submitFeedbackSaga(action: SubmitFeedbackRequestAction) {
  try {
    // Create optimistic feedback entry
    const user = getLoggedinUser();
    const feedbackEntry: FeedbackEntry = {
      id: uuidv4(),
      text: action.payload.feedbackText,
      date: new Date().toISOString(),
      author: user?.name || "Current User",
      // Note: We don't handle file attachments optimistically for now
    };

    // Dispatch optimistic update immediately
    yield put(
      actions.optimisticUpdateFeedback(
        action.payload.targetType,
        action.payload.targetId,
        feedbackEntry,
      ),
    );

    // Make API call
    const response: {
      success: boolean;
      message: string;
      updatedCategories: WorkflowCategoryData[];
    } = yield call(api.submitFeedback, action.payload);

    if (response.success) {
      // Success - just mark loading as false, keep optimistic update
      yield put(actions.submitFeedbackSuccess(response.updatedCategories));
    } else {
      // Even on failure, we keep the optimistic update as requested
      yield put(actions.submitFeedbackFailure(response.message));
    }
  } catch (error: any) {
    // Even on error, we keep the optimistic update as requested
    yield put(
      actions.submitFeedbackFailure(
        error.message || "Failed to submit feedback",
      ),
    );
  }
}

// Delete Step Saga
function* deleteStepSaga(action: DeleteStepRequestAction) {
  try {
    // Dispatch optimistic update immediately
    yield put(actions.optimisticDeleteStep(action.payload));

    // Make API call
    const response: WorkflowCategoryData[] = yield call(
      api.deleteWorkflowStep,
      action.payload,
    );

    // Success - just mark loading as false, keep optimistic update
    yield put(actions.deleteStepSuccess(response));
  } catch (error: any) {
    // Even on failure, we keep the optimistic update as requested
    yield put(
      actions.deleteStepFailure(error.message || "Failed to delete step"),
    );
  }
}

// Watchers
function* watchFetchWorkflows() {
  yield takeEvery(
    ProcessActionTypes.FETCH_WORKFLOWS_REQUEST,
    fetchWorkflowsSaga,
  );
}

function* watchSubmitStepConfig() {
  yield takeEvery(
    ProcessActionTypes.SUBMIT_STEP_CONFIG_REQUEST,
    submitStepConfigSaga,
  );
}

function* watchSubmitFeedback() {
  yield takeEvery(
    ProcessActionTypes.SUBMIT_FEEDBACK_REQUEST,
    submitFeedbackSaga,
  );
}

function* watchDeleteStep() {
  yield takeEvery(ProcessActionTypes.DELETE_STEP_REQUEST, deleteStepSaga);
}

// Root Process Saga
function* processSaga() {
  yield all([
    fork(watchFetchWorkflows),
    fork(watchSubmitStepConfig),
    fork(watchSubmitFeedback),
    fork(watchDeleteStep),
  ]);
}

export default processSaga;
